import { useMemo, useState, type ReactNode } from 'react'
import { MERGE_TAGS } from '@/shared/email/mergeFields'
import {
  EVENT_LABELS,
  type Block,
  type Flow,
} from '../../../../functions-webhook/src/automation/flows'
import type { Newsletter } from '../repository/newsletterRepository'
import { Icon, type IconName } from './BlockIcon'
import { TestMode } from './TestMode'
import {
  REENTRY_LABEL,
  SIDE_X,
  STAGE_W,
  blockKind,
  canStart,
  blockTitle,
  countsOf,
  dayMonth,
  layoutFlow,
  ruleText,
  tagsIn,
  timingOf,
  triggerKind,
  type Placed,
} from './flowText'
import { PERIOD_LABEL, useQueues, useWaitingNow } from './useAutomationStats'

/**
 * One automation: its canvas, its settings, and (stage 4) test mode.
 *
 * Artboards 2 and 3 of the design canvas, drawn from the flow definition —
 * the blocks are code, so the canvas cannot show a flow the engine does not
 * run. What is set here is what the CRM stores: the switch and the email.
 */

export type FlowTab = 'canvas' | 'settings' | 'test'

const PLAN_URL = 'https://claude.ai/artifact/75Xri3iAp6Q9FnnrH1QcT8'
const KNOWN_TAGS = new Set(MERGE_TAGS.map((t) => t.tag))
const TYPE_LABEL = { transactional: 'Transactional', lifecycle: 'Lifecycle', marketing: 'Marketing' } as const
const STREAM_LABEL = { automation: 'Account stream', newsletter: 'Newsletter stream' } as const

const ICON: Record<string, { icon: IconName; tone: 'blue' | 'grey' | 'indigo' }> = {
  trigger: { icon: 'zap', tone: 'blue' },
  wait: { icon: 'clock', tone: 'grey' },
  batch: { icon: 'layers', tone: 'grey' },
  condition: { icon: 'branch', tone: 'indigo' },
  send: { icon: 'mail', tone: 'blue' },
  task: { icon: 'task', tone: 'grey' },
  exit: { icon: 'exit', tone: 'grey' },
}

const PALETTE: { label: string; kind: string; icon: IconName }[] = [
  { label: 'Trigger', kind: 'trigger', icon: 'zap' },
  { label: 'Wait', kind: 'wait', icon: 'clock' },
  { label: 'Condition', kind: 'condition', icon: 'branch' },
  { label: 'Send email', kind: 'send', icon: 'mail' },
  { label: 'Update field', kind: 'wait', icon: 'pen' },
  { label: 'Create task', kind: 'task', icon: 'task' },
  { label: 'Exit', kind: 'exit', icon: 'exit' },
]

/** Tags a template uses that the CRM cannot fill. Switching on waits for none. */
export function missingTags(template: Newsletter | undefined): string[] {
  if (!template) return []
  return tagsIn(`${template.subject} ${template.html}`)
    .map((t) => t.tag)
    .filter((t) => !KNOWN_TAGS.has(t))
}

interface Props {
  flow: Flow
  tab: FlowTab
  onTab: (tab: FlowTab) => void
  onBack: () => void
  on: boolean
  canEdit: boolean
  busy: boolean
  templateId: string
  drafts: Newsletter[]
  counts: Record<string, number> | undefined
  error: string | null
  onToggle: () => void
  onChoose: (templateId: string) => void
}

export function FlowDetail(p: Props) {
  const { flow } = p
  const template = p.drafts.find((d) => d.id === p.templateId)

  return (
    <main className="flowd">
      <header className="flowd__head">
        <button type="button" className="flowd__back" onClick={p.onBack}>
          ← Email automation
        </button>
        <div className="flowd__titlerow">
          <h2 className="flowd__title">
            {flow.id} {flow.name}
          </h2>
          <span className="flowd__chip">{flow.folder}</span>
          <span className="flowd__chip">{TYPE_LABEL[flow.type]}</span>
          <span className="flowd__chip flowd__chip--blue">{STREAM_LABEL[flow.stream]}</span>
          <span className="flowd__spacer" />
          <a className="flowd__planlink" href={PLAN_URL} target="_blank" rel="noreferrer">
            Plan row {flow.id} <Icon name="external" size={14} />
          </a>
          <span className="flowd__switchwrap">
            <span className="flowd__switchlabel">{p.on ? 'On' : 'Off'}</span>
            <button
              type="button"
              role="switch"
              aria-checked={p.on}
              aria-label={`${p.on ? 'Switch off' : 'Switch on'} ${flow.id} ${flow.name}`}
              className={`autom__switch${p.on ? ' autom__switch--on' : ''}`}
              disabled={!p.canEdit || p.busy}
              title={p.canEdit ? undefined : 'An admin sets these.'}
              onClick={p.onToggle}
            >
              <span />
            </button>
          </span>
        </div>
        <nav className="flowd__tabs" aria-label="Automation views">
          {(
            [
              ['canvas', 'Canvas'],
              ['settings', 'Settings'],
              ['test', 'Test mode'],
            ] as const
          ).map(([k, label]) => (
            <button
              key={k}
              type="button"
              className="flowd__tab"
              aria-current={p.tab === k ? 'page' : undefined}
              onClick={() => p.onTab(k)}
            >
              {label}
            </button>
          ))}
        </nav>
      </header>

      {p.error && <div className="empty-note flowd__error">{p.error}</div>}

      {p.tab === 'canvas' && <CanvasView {...p} template={template} />}
      {p.tab === 'settings' && <SettingsView flow={flow} onCanvas={() => p.onTab('canvas')} />}
      {p.tab === 'test' && <TestMode flow={flow} template={template} />}
    </main>
  )
}

// ---------------------------------------------------------------------------
// Canvas
// ---------------------------------------------------------------------------

function CanvasView(p: Props & { template: Newsletter | undefined }) {
  const { flow } = p
  const layout = useMemo(() => layoutFlow(flow), [flow])
  const firstCondition = flow.blocks.find((b) => b.kind === 'condition')
  const [selected, setSelected] = useState<string>(firstCondition?.id ?? 'trigger')
  // A phone starts zoomed out, so the whole column fits before anybody scrolls.
  const [zoom, setZoom] = useState(() => (window.innerWidth < 700 ? 50 : 100))

  // Runs waiting at a wait or a batch are stored at the block they wake at.
  const waits = useMemo(
    () =>
      flow.blocks.flatMap((b, i) => {
        const next = flow.blocks[i + 1]
        if (!next || (b.kind !== 'wait' && b.kind !== 'batch')) return []
        return [{ block: b.id, at: next.id, status: b.kind === 'batch' ? ('batch' as const) : ('waiting' as const) }]
      }),
    [flow],
  )
  const waiting = useWaitingNow(flow.id, waits)
  const value = (key: string, blockId: string): string => {
    if (key === 'live') {
      const w = waits.find((x) => x.block === blockId)
      const n = w ? waiting[`${w.status}:${w.at}`] : undefined
      return n === undefined ? '–' : n.toLocaleString('en-GB')
    }
    if (!p.counts) return '–'
    return (p.counts[key] ?? 0).toLocaleString('en-GB')
  }

  const sel = layout.placed.find((x) => x.id === selected) ?? layout.placed[0]!
  const scale = zoom / 100

  return (
    <div className="flowd__body">
      <aside className="flowd__palette" aria-label="Blocks">
        <h3>Blocks</h3>
        <p className="flowd__hint">
          A flow’s blocks are part of its definition, so the canvas always shows what the engine
          runs. Adding or moving a block is a developer change for now.
        </p>
        {PALETTE.map((b) => (
          <div key={b.label} className="flowd__pal">
            <span className={`flowd__ic flowd__ic--${ICON[b.kind]!.tone} flowd__ic--sm`}>
              <Icon name={b.icon} size={16} />
            </span>
            {b.label}
          </div>
        ))}
      </aside>

      <section className="flowd__flow">
        <div className="flowd__toolbar">
          <button type="button" className="flowd__rulechip" onClick={() => p.onTab('settings')}>
            <Icon name="check" size={14} />
            {REENTRY_LABEL[flow.reentry]} ·{' '}
            {flow.type === 'transactional'
              ? 'transactional, not counted toward the cap'
              : 'counts toward the cap of 1 a day, 2 in 7 days'}
          </button>
          <div className="flowd__zoom">
            <span className="flowd__muted flowd__small">{PERIOD_LABEL}</span>
            <button type="button" className="flowd__zbtn" aria-label="Zoom out" disabled={zoom <= 50} onClick={() => setZoom((z) => z - 10)}>
              <Icon name="minus" />
            </button>
            <span className="flowd__zval">{zoom}%</span>
            <button type="button" className="flowd__zbtn" aria-label="Zoom in" disabled={zoom >= 150} onClick={() => setZoom((z) => z + 10)}>
              <Icon name="plus" />
            </button>
          </div>
        </div>
        <FlowStage
          flow={flow}
          template={p.template}
          layout={layout}
          scale={scale}
          selected={sel.id}
          onSelect={setSelected}
          line={(b) =>
            countsOf(b.item)
              .map((c) => `${c.label} ${value(c.key, b.id)}`)
              .join(' · ')
          }
        />
      </section>

      <aside className="flowd__panel" aria-label="Block settings">
        <BlockPanel {...p} b={sel} value={value} />
      </aside>
    </div>
  )
}

/** The drawn flow: lines, then blocks. Shared by the canvas and test mode. */
export interface BlockLook {
  state: 'idle' | 'passed' | 'here' | 'skipped'
  note: string
}

export function FlowStage(props: {
  flow: Flow
  template: Newsletter | undefined
  layout: ReturnType<typeof layoutFlow>
  scale: number
  selected?: string
  onSelect?: (id: string) => void
  line: (b: Placed) => string
  look?: (id: string) => BlockLook
  /** Lines along the path taken, drawn in the primary colour. */
  litEdge?: (from: string, to: string) => boolean
}) {
  const { layout, scale } = props
  return (
    <div className="flowd__scroll">
      <div className="flowd__sizer" style={{ width: STAGE_W * scale, height: layout.height * scale }}>
        <div
          className="flowd__stage"
          style={{ width: STAGE_W, height: layout.height, transform: `scale(${scale})` }}
        >
          <svg className="flowd__lines" width={STAGE_W} height={layout.height} aria-hidden="true">
            <defs>
              <marker id="flowd-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                <path d="M0 0 10 5 0 10z" fill="#AEB1B4" />
              </marker>
              <marker id="flowd-arrow-on" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                <path d="M0 0 10 5 0 10z" fill="#3456DA" />
              </marker>
            </defs>
            {layout.edges.map((e) => {
              const lit = props.litEdge?.(e.from, e.to) ?? false
              return (
                <g key={`${e.from}-${e.to}`}>
                  <path
                    d={
                      e.shape === 'down'
                        ? `M${e.x1} ${e.y1} V${e.y2 - 2}`
                        : `M${e.x1} ${e.y1} H${e.x2} V${e.y2 - 2}`
                    }
                    fill="none"
                    stroke={lit ? '#3456DA' : '#AEB1B4'}
                    strokeWidth={2}
                    strokeLinecap="round"
                    markerEnd={lit ? 'url(#flowd-arrow-on)' : 'url(#flowd-arrow)'}
                  />
                  {e.label && (
                    <text
                      className="flowd__branch"
                      x={e.shape === 'down' ? e.x1 + 10 : SIDE_X + 8}
                      y={e.shape === 'down' ? e.y1 + 40 : e.y1 - 8}
                    >
                      {e.label}
                    </text>
                  )}
                </g>
              )
            })}
          </svg>
          {layout.placed.map((b) => (
            <BlockCard
              key={b.id}
              b={b}
              flow={props.flow}
              template={props.template}
              selected={props.selected === b.id}
              onSelect={props.onSelect ? () => props.onSelect!(b.id) : undefined}
              line={props.line(b)}
              look={props.look?.(b.id)}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

function iconOf(item: Placed['item']) {
  return ICON[item === 'trigger' ? 'trigger' : item.kind]!
}

function BlockCard(props: {
  b: Placed
  flow: Flow
  template: Newsletter | undefined
  selected: boolean
  onSelect?: () => void
  line: string
  look?: BlockLook
}) {
  const { b, flow, template } = props
  const ic = iconOf(b.item)
  const kind = b.item === 'trigger' ? triggerKind(flow.trigger) : blockKind(b.item)
  const title =
    b.item === 'trigger'
      ? flow.summary
      : b.item.kind === 'send' && template
        ? template.subject
        : blockTitle(b.item)
  const missing = b.item !== 'trigger' && b.item.kind === 'send' ? missingTags(template) : []
  const small = b.side || (b.item !== 'trigger' && b.item.kind === 'exit')

  return (
    <button
      type="button"
      className={`flowd__block${small ? ' flowd__block--small' : ''}${props.look ? ` flowd__block--${props.look.state}` : ''}`}
      style={{ left: b.x, top: b.y, width: b.w, height: b.h }}
      aria-pressed={props.onSelect ? props.selected : undefined}
      tabIndex={props.onSelect ? undefined : -1}
      onClick={props.onSelect}
    >
      {b.item !== 'trigger' && b.item.kind === 'send' ? (
        <Thumb />
      ) : (
        <span className={`flowd__ic flowd__ic--${ic.tone}${small ? ' flowd__ic--sm' : ''}`}>
          <Icon name={ic.icon} size={small ? 16 : 20} />
        </span>
      )}
      <span className="flowd__btext">
        <span className="flowd__bkind">{kind}</span>
        <span className="flowd__btitle">{title}</span>
        {b.item !== 'trigger' && b.item.kind === 'send' && !template && (
          <span className="flowd__warnchip">No email chosen</span>
        )}
        {missing.length > 0 && (
          <span className="flowd__warnchip">
            {missing.length} merge tag{missing.length === 1 ? '' : 's'} missing
          </span>
        )}
        {props.look?.note ? (
          <span className="flowd__bnote">{props.look.note}</span>
        ) : (
          props.line && <span className="flowd__bcount">{props.line}</span>
        )}
      </span>
    </button>
  )
}

/** The template thumbnail from the design: logo, picture, headline, text, button. */
function Thumb() {
  return (
    <svg className="flowd__thumb" width="72" height="100" viewBox="0 0 88 120" aria-hidden="true">
      <rect x="0.5" y="0.5" width="87" height="119" rx="8" fill="#fff" stroke="#D7DDE3" />
      <rect x="10" y="10" width="22" height="5" rx="2" fill="#000B38" />
      <rect x="10" y="22" width="68" height="34" rx="3" fill="#C2CCF4" />
      <rect x="10" y="64" width="56" height="6" rx="2" fill="#111212" />
      <rect x="10" y="76" width="68" height="3" rx="1.5" fill="#AEB1B4" />
      <rect x="10" y="83" width="60" height="3" rx="1.5" fill="#AEB1B4" />
      <rect x="10" y="96" width="36" height="12" rx="6" fill="#3456DA" />
    </svg>
  )
}

// ---------------------------------------------------------------------------
// Block panel
// ---------------------------------------------------------------------------

function Stat({ label, v }: { label: string; v: string }) {
  return (
    <div className="flowd__stat">
      <span>{label}</span>
      <strong>{v}</strong>
    </div>
  )
}

function Field({ label, children, stack }: { label: string; children: ReactNode; stack?: boolean }) {
  return (
    <div className={`flowd__field${stack ? ' flowd__field--stack' : ''}`}>
      <span className="flowd__flabel">{label}</span>
      <span className="flowd__fvalue">{children}</span>
    </div>
  )
}

function BlockPanel(
  p: Props & { template: Newsletter | undefined; b: Placed; value: (key: string, blockId: string) => string },
) {
  const { flow, b } = p
  const stats = (suffix: string) =>
    countsOf(b.item).map((c) => (
      <Stat key={c.key} label={`${c.label}${c.key === 'live' ? ' now' : suffix}`} v={p.value(c.key, b.id)} />
    ))
  const footer = <div className="flowd__stats">{stats(', last 30 days')}</div>

  if (b.item === 'trigger') {
    const t = flow.trigger
    return (
      <>
        <PanelHead kind={triggerKind(t)} title={flow.summary} />
        {t.kind === 'event' && (
          <>
            <Field label="Starts when">{EVENT_LABELS[t.event]}</Field>
            <Field label="Timing">{timingOf(t)}</Field>
            {t.from === 'expiresAt' && (
              <p className="flowd__callout">
                The end date comes from the store: the purchase carries it, so a campaign with a
                longer trial needs no change here.
              </p>
            )}
          </>
        )}
        {t.kind === 'date' && (
          <>
            <Field label="Starts on">Every year, by the contact’s hemisphere</Field>
            <Field label="North">{dayMonth(t.north)}</Field>
            <Field label="South">{dayMonth(t.south)}</Field>
            <p className="flowd__muted flowd__small">No hemisphere on file: north schedule.</p>
          </>
        )}
        {t.kind === 'before-season' && <Field label="Starts">{t.days} days before the season starts</Field>}
        {t.kind === 'monthly-in-season' && <Field label="Starts">First Thursday of each month in season</Field>}
        {t.kind === 'inactive' && <Field label="Starts">No app open for {t.days} days, in season</Field>}
        {t.kind === 'event' && t.event === 'account.created' && (
          <p className="flowd__callout">
            New accounts are picked up every five minutes, ten minutes after they are made, so the
            app has written the name and email. Only accounts made after the flow is switched on.
          </p>
        )}
        {!canStart(flow) && (
          <p className="flowd__callout">Not running yet: this kind of start waits for the app’s own events.</p>
        )}
        {footer}
      </>
    )
  }

  const item: Block = b.item
  switch (item.kind) {
    case 'wait':
      return (
        <>
          <PanelHead kind="Wait" title={blockTitle(item)} />
          {'hours' in item ? (
            <Field label="Wait">{blockTitle(item)}</Field>
          ) : (
            <>
              <Field label="Wait until">The contact’s send window</Field>
              <p className="flowd__callout">
                Weekdays 08:00–10:00, weekends 07:00–09:00, in the contact’s local time. No time
                zone on file: Europe/Oslo.
              </p>
            </>
          )}
          {footer}
        </>
      )
    case 'batch':
      return (
        <>
          <PanelHead kind="Wait · batch" title="Send in daily batches" />
          <Field label="Who goes first">Most active users first</Field>
          <p className="flowd__callout">
            At most 3,500 per rolling 24 hours, out of the 4,500 on news.skida.app. Triggered
            emails such as T05 and T13 always go before batches. Transactional mail doesn’t count
            toward the limit.
          </p>
          <QueueState flowId={flow.id} />
          {footer}
        </>
      )
    case 'condition': {
      const next = flow.blocks[flow.blocks.indexOf(item) + 1]
      const no = flow.blocks.find((x) => x.id === item.no)
      return (
        <>
          <PanelHead kind="Condition" title={item.label} />
          <div className="flowd__field flowd__field--stack">
            <span className="flowd__flabel">Continue only if</span>
            <ul className="flowd__rules">
              {item.rules.map((r, i) => (
                <li key={i}>
                  {i > 0 && <span className="flowd__and">and </span>}
                  {ruleText(r)}
                </li>
              ))}
            </ul>
          </div>
          <Field label="Yes">Continue to {next ? blockKind(next) : 'the end'}</Field>
          <Field label="No">Exit · {no ? blockTitle(no) : item.no}</Field>
          <p className="flowd__callout">
            Checked right before sending, so anyone whose situation changes during the wait drops
            out without an email.
          </p>
          {footer}
        </>
      )
    }
    case 'send':
      return <SendPanel {...p} item={item} footer={footer} />
    case 'task':
      return (
        <>
          <PanelHead kind="Create task" title={item.label} />
          <p className="flowd__callout">Tasks are created in a later step; the flow passes this block for now.</p>
          {footer}
        </>
      )
    case 'exit':
      return (
        <>
          <PanelHead kind="Exit" title={item.reason} />
          <p className="flowd__muted">
            The contact leaves {flow.id} here. {REENTRY_LABEL[flow.reentry]} — they can come back in
            only as often as that allows.
          </p>
          {footer}
        </>
      )
  }
}

/** How far each season's queue has got. Built on the start date, not before. */
function QueueState({ flowId }: { flowId: string }) {
  const queues = useQueues(flowId)
  if (queues.length === 0) {
    return (
      <p className="flowd__muted flowd__small">
        The queue is built on the start date, once the flow is switched on: everybody who takes this
        stream, most active first.
      </p>
    )
  }
  return (
    <>
      {queues.map((q) => (
        <Field key={q.id} label={`Queue ${q.season} (${q.hemisphere})`}>
          {q.next.toLocaleString('en-GB')} of {q.total.toLocaleString('en-GB')} let out
        </Field>
      ))}
    </>
  )
}

function PanelHead({ kind, title }: { kind: string; title: string }) {
  return (
    <div className="flowd__phead">
      <span className="flowd__bkind">{kind}</span>
      <h3>{title}</h3>
    </div>
  )
}

function SendPanel(
  p: Props & { template: Newsletter | undefined; item: Extract<Block, { kind: 'send' }>; footer: ReactNode },
) {
  const { template, flow } = p
  const tags = template ? tagsIn(`${template.subject} ${template.html}`) : []
  const missing = missingTags(template)
  return (
    <>
      <PanelHead kind="Send email" title={template?.subject || p.item.subject} />
      <label className="flowd__field flowd__field--stack">
        <span className="flowd__flabel">Email (a Skida App draft from the Newsletter composer)</span>
        <select
          className="input"
          value={p.templateId}
          disabled={!p.canEdit || p.busy}
          onChange={(e) => p.onChoose(e.target.value)}
        >
          <option value="">Choose a draft…</option>
          {p.templateId && !template && <option value={p.templateId}>Not a draft any more</option>}
          {p.drafts.map((d) => (
            <option key={d.id} value={d.id}>
              {d.name || d.subject || '(no subject)'}
            </option>
          ))}
        </select>
      </label>
      {!template && (
        <p className="flowd__muted flowd__small">
          Suggested subject from the plan: “{p.item.subject}”. Write it as a draft, then choose it
          here.
        </p>
      )}
      {template && (
        <div className="flowd__field flowd__field--stack">
          <span className="flowd__flabel">Merge tags</span>
          <span className="flowd__tags">
            {tags.length === 0 && <span className="flowd__muted flowd__small">None</span>}
            {tags.map((t) => (
              <span
                key={t.tag}
                className={`flowd__tag${KNOWN_TAGS.has(t.tag) ? '' : ' flowd__tag--missing'}`}
              >
                {t.shown}
                {KNOWN_TAGS.has(t.tag) ? '' : ' · missing'}
              </span>
            ))}
          </span>
        </div>
      )}
      {missing.length > 0 && (
        <div className="flowd__warn">
          <Icon name="alert" size={18} />
          <span>
            <strong>
              {missing.length} merge tag{missing.length === 1 ? ' is' : 's are'} not in the CRM
            </strong>
            <br />
            {missing.join(' and ')} {missing.length === 1 ? 'has' : 'have'} no data. Rewrite the
            email without {missing.length === 1 ? 'it' : 'them'} before switching on.
          </span>
        </div>
      )}
      <p className="flowd__muted flowd__small">
        Tagged automation={flow.id}, so opens and clicks count on this block.
      </p>
      <div className="flowd__actions">
        {template && (
          <a className="btn btn--primary" href={`/marketing/newsletter?draft=${encodeURIComponent(template.id)}`}>
            Open in editor
          </a>
        )}
      </div>
      {p.footer}
    </>
  )
}

// ---------------------------------------------------------------------------
// Settings — artboard 3, read from the definition
// ---------------------------------------------------------------------------

function SettingsView({ flow, onCanvas }: { flow: Flow; onCanvas: () => void }) {
  return (
    <div className="flowd__main">
      <p className="flowd__callout">
        These settings are part of {flow.id}’s definition, the same one the engine runs, and change
        with an update from the developer. What you set in the CRM is the switch and the email, on
        the canvas.
      </p>
      <section className="flowd__card">
        <h3>General</h3>
        <Field label="Name">
          {flow.id} {flow.name}
        </Field>
        <Field label="Folder">{flow.folder}</Field>
        <Field label="Plan row">
          <a href={PLAN_URL} target="_blank" rel="noreferrer">
            Skida – Email Lifecycle Automation › {flow.id}
          </a>
        </Field>
      </section>
      <section className="flowd__card">
        <h3>Re-entry</h3>
        <p className="flowd__muted">How often one contact can go through this automation.</p>
        <Field label="Rule">{REENTRY_LABEL[flow.reentry]}</Field>
      </section>
      <section className="flowd__card">
        <h3>Frequency cap</h3>
        <p className="flowd__muted">Shared by every lifecycle and marketing automation.</p>
        {flow.type === 'transactional' ? (
          <Field label="Cap">Not counted — a transactional email always goes</Field>
        ) : (
          <Field label="Cap" stack>
            Counts toward the shared cap: at most 1 email a day and 2 in any 7 days per contact. If
            the cap is full, the email waits for the next free day.
          </Field>
        )}
      </section>
      <section className="flowd__card">
        <h3>Send window</h3>
        <p className="flowd__muted">In the contact’s local time. No time zone on file: Europe/Oslo.</p>
        <Field label="Weekdays">08:00–10:00</Field>
        <Field label="Weekends">07:00–09:00</Field>
        {flow.type === 'transactional' && (
          <p className="flowd__muted flowd__small">
            A transactional flow without a Wait block sends at once, outside the window.
          </p>
        )}
      </section>
      <section className="flowd__card">
        <h3>Email type and stream</h3>
        <p className="flowd__muted">
          The stream decides which choice on the preference page turns this email off.
        </p>
        <Field label="Type">{TYPE_LABEL[flow.type]}</Field>
        <Field label="Stream">
          {flow.stream === 'automation'
            ? 'Account — “Only what concerns my account”'
            : 'Newsletter — “News, tips and offers”'}
        </Field>
        {flow.type === 'marketing' && (
          <p className="flowd__callout">Marketing emails go only to contacts with marketing consent on file.</p>
        )}
        <Field label="Daily limit">
          {flow.type === 'transactional'
            ? 'Not counted toward the 4,500 a day'
            : 'Counted toward the 4,500 a day on news.skida.app'}
        </Field>
      </section>
      <div className="flowd__actions flowd__actions--end">
        <button type="button" className="btn btn--ghost" onClick={onCanvas}>
          Back to the canvas
        </button>
      </div>
    </div>
  )
}
